import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { artworkJobsApi } from '../lib/api';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    ArrowLeft, Download, CheckCircle2, XCircle, 
    Clock, AlertCircle, FileImage, Palette, Upload, File as FileIcon, Eye,
    Share2, MessageSquare
} from 'lucide-react';
import { ShareArtworkDialog } from '../components/ShareArtworkDialog';

const ARTWORK_STATUS_LABELS = {
    'uploaded': { label: 'Pending Review', color: 'bg-blue-100 text-blue-700' },
    'in_review': { label: 'In Review', color: 'bg-amber-100 text-amber-700' },
    'rejected': { label: 'Revision Needed', color: 'bg-red-100 text-red-700' },
    'approved': { label: 'All Approved', color: 'bg-emerald-100 text-emerald-700' },
    'corrected': { label: 'Corrected', color: 'bg-purple-100 text-purple-700' },
};

const FILE_STATUS_CONFIG = {
    'pending': { label: 'Pending', icon: Clock, color: 'bg-slate-100 text-slate-600', border: 'border-slate-200' },
    'approved': { label: 'Approved', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200 bg-emerald-50/30' },
    'rejected': { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700', border: 'border-red-200 bg-red-50/30' },
};

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function CustomerArtworkReviewPage() {
    const { t } = useTranslation();
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [reviewData, setReviewData] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [comment, setComment] = useState('');
    // Per-file reject dialog state. Replaces the legacy global reject flow
    // for corrected artwork — admins/customers asked us to give per-file
    // approve / request-changes / upload-own-replacement control.
    const [perFileRejectFor, setPerFileRejectFor] = useState(null); // file or null
    const [perFileComment, setPerFileComment] = useState('');
    const [actingFileId, setActingFileId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingFileId, setUploadingFileId] = useState(null);
    const fileInputRefs = useRef({});
    const customerReplaceInputRefs = useRef({});

    const fetchReviewData = async () => {
        try {
            const { data } = await artworkJobsApi.getCustomerReview(orderId);
            setReviewData(data);
            setLoadError(null);
        } catch (error) {
            if (error.response?.status === 404) {
                setReviewData(null);
                setLoadError(null);
            } else {
                // Don't silently swallow 5xx / network / parse errors — surface
                // them so the user sees a friendly message instead of a blank
                // page if rendering can't recover.
                console.error('[CustomerArtworkReviewPage] Failed to load review:', error);
                setReviewData(null);
                setLoadError(
                    error.response?.data?.detail
                    || error.message
                    || 'Could not load artwork review for this order.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReviewData(); }, [orderId]);

    const handleApprove = async () => {
        setIsSubmitting(true);
        try {
            await artworkJobsApi.submitCustomerReview(orderId, 'approve', comment);
            toast.success(t('artwork_review.corrected_approved', 'Corrected artwork approved'));
            fetchReviewData();
            setIsApproveDialogOpen(false);
            setComment('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!comment.trim()) {
            toast.error(t('artwork_review.provide_feedback', 'Please provide feedback for the requested changes'));
            return;
        }
        setIsSubmitting(true);
        try {
            await artworkJobsApi.submitCustomerReview(orderId, 'request_changes', comment);
            toast.success(t('artwork_review.changes_submitted', 'Change request submitted'));
            fetchReviewData();
            setIsRejectDialogOpen(false);
            setComment('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReplaceFile = async (fileId, e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        if (!reviewData?.job_id) {
            toast.error(t('artwork_review.no_job', 'No artwork job found'));
            return;
        }
        
        setUploadingFileId(fileId);
        try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append('files', files[i]);
            }
            await artworkJobsApi.replaceFile(reviewData.job_id, fileId, formData);
            toast.success(t('artwork_review.replacement_uploaded', 'Replacement uploaded successfully'));
            fetchReviewData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload replacement');
        } finally {
            setUploadingFileId(null);
            if (fileInputRefs.current[fileId]) fileInputRefs.current[fileId].value = '';
        }
    };

    // ---- Per-file actions for CORRECTED artwork (repro_files) ----

    const handleApproveCorrectedFile = async (fileId) => {
        setActingFileId(fileId);
        try {
            await artworkJobsApi.approveCorrectedFile(reviewData.job_id, fileId);
            toast.success(t('artwork_review.file_approved', 'File approved'));
            fetchReviewData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve file');
        } finally {
            setActingFileId(null);
        }
    };

    const handleRejectCorrectedFileSubmit = async () => {
        if (!perFileRejectFor) return;
        if (!perFileComment.trim()) {
            toast.error(t('artwork_review.describe_changes', 'Please describe the changes you need'));
            return;
        }
        setIsSubmitting(true);
        try {
            await artworkJobsApi.rejectCorrectedFile(
                reviewData.job_id,
                perFileRejectFor.id,
                perFileComment.trim(),
            );
            toast.success(t('artwork_review.changes_sent_to_repro', 'Change request sent to repro partner'));
            setPerFileRejectFor(null);
            setPerFileComment('');
            fetchReviewData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit feedback');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCustomerReplaceCorrected = async (fileId, e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setActingFileId(fileId);
        try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
            await artworkJobsApi.customerReplaceCorrectedFile(reviewData.job_id, fileId, formData);
            toast.success(t('artwork_review.replacement_notified', 'Your replacement was uploaded — repro partner notified'));
            fetchReviewData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload replacement');
        } finally {
            setActingFileId(null);
            if (customerReplaceInputRefs.current[fileId]) customerReplaceInputRefs.current[fileId].value = '';
        }
    };

    const handleDownloadCorrected = async (fileId, filename) => {
        try {
            const token = localStorage.getItem('token');
            const API = API_BASE;
            const response = await fetch(`${API}/api/orders/${orderId}/artwork-review/download/${fileId}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Download failed');
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const data = await response.json();
                if (data.download_url) {
                    window.open(data.download_url, '_blank');
                } else {
                    throw new Error('No download URL returned');
                }
            } else {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename || 'artwork');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Download error:', error);
            toast.error(t('artwork_review.download_failed', 'Failed to download file'));
        }
    };

    // Inline preview for PDFs (and other browser-renderable types) — opens a
    // presigned `Content-Disposition: inline` URL in a new tab so the browser
    // renders the file in its built-in viewer instead of downloading it.
    const handlePreviewCorrected = async (fileId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE}/api/orders/${orderId}/artwork-review/preview-url/${fileId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Preview failed');
            }
            const data = await response.json();
            if (!data.preview_url) throw new Error('No preview URL returned');
            window.open(data.preview_url, '_blank', 'noopener');
        } catch (error) {
            console.error('Preview error:', error);
            toast.error(error?.message || 'Failed to open preview');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="container mx-auto px-4 py-6">
                    <p className="text-center text-muted-foreground py-12">{t('artwork_review.loading', 'Loading artwork review...')}</p>
                </main>
            </div>
        );
    }

    if (!reviewData) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="container mx-auto px-4 py-6 max-w-4xl">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/${orderId}/files`)} className="mb-4">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Order
                    </Button>
                    <Card>
                        <CardContent className="py-12 text-center">
                            {loadError ? (
                                <>
                                    <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                                    <h2 className="text-lg font-medium text-slate-700" data-testid="artwork-review-load-error">
                                        Could not load artwork review
                                    </h2>
                                    <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto break-words">{loadError}</p>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="mt-4"
                                        onClick={() => { setLoading(true); fetchReviewData(); }}
                                        data-testid="artwork-review-retry-btn"
                                    >
                                        Retry
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Palette className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                    <h2 className="text-lg font-medium text-slate-600">{t('artwork_review.no_job_title', 'No Artwork Job')}</h2>
                                    <p className="text-sm text-slate-400 mt-2">
                                        Artwork review will appear once artwork has been submitted for this order.
                                    </p>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </main>
            </div>
        );
    }

    const artworkStatus = reviewData.artwork_status;
    const statusLabel = ARTWORK_STATUS_LABELS[artworkStatus] || ARTWORK_STATUS_LABELS['uploaded'];
    const artworkFiles = reviewData.artwork_files || [];
    const supersededFiles = reviewData.superseded_files || [];
    const correctedFiles = reviewData.corrected_files || [];
    const canReview = reviewData.can_review;
    const hasRejected = reviewData.has_rejected_files;
    const rejectedFiles = artworkFiles.filter(f => f.review_status === 'rejected');

    // Customer-uploaded replacement files that are waiting for the repro
    // partner to deliver a corrected version. These do NOT belong in the
    // "Corrected Artwork" approval list — the customer can't approve their
    // own upload; they're waiting on repro.
    const awaitingReproFiles = correctedFiles.filter(
        (f) => !f.superseded
            && f.source === 'customer_replacement'
            && f.review_status === 'awaiting_repro',
    );
    const awaitingReproIds = new Set(awaitingReproFiles.map((f) => f.id));
    const reviewableCorrectedFiles = correctedFiles.filter(
        (f) => !awaitingReproIds.has(f.id),
    );
    
    // Group superseded files by the file they were replaced by (for version history)
    const getVersionHistory = (fileId) => {
        // Find all superseded files that were replaced to eventually become this file
        const history = [];
        const findPrevious = (replacesId) => {
            const prev = supersededFiles.find(f => f.id === replacesId);
            if (prev) return prev;
            // Also check if any superseded file was replaced by this ID
            return supersededFiles.find(f => {
                // Find files that this current file replaced
                return artworkFiles.some(a => a.id === fileId && a.replaces_file_id === f.id);
            });
        };
        // Direct: find superseded files that this file replaces
        const directPrev = supersededFiles.filter(f => {
            // Current file replaces this superseded file
            const currentFile = artworkFiles.find(a => a.id === fileId);
            return currentFile && currentFile.replaces_file_id === f.id;
        });
        // Also find chain: superseded files that were replaced by other superseded files
        const addChain = (fid) => {
            const prev = supersededFiles.filter(sf => {
                return supersededFiles.some(s => s.replaces_file_id === sf.id) || 
                       artworkFiles.some(a => a.replaces_file_id === sf.id && a.id === fid);
            });
            return prev;
        };
        return [...directPrev].sort((a, b) => (b.version || 0) - (a.version || 0));
    };

    // When the customer has uploaded a replacement but no other corrected
    // file is still pending their review, the page is effectively waiting on
    // repro — surface that prominently in the header badge and banner so
    // the customer doesn't think they still need to do something.
    const allRemainingAwaitRepro =
        awaitingReproFiles.length > 0
        && reviewableCorrectedFiles.filter(
            (f) => !f.superseded && (f.review_status || 'pending') === 'pending',
        ).length === 0;
    const headerStatusLabel = allRemainingAwaitRepro
        ? { label: 'In Review', color: 'bg-amber-100 text-amber-700' }
        : statusLabel;

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-6 max-w-4xl">
                <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/${orderId}/files`)} className="mb-4" data-testid="back-to-order-btn">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back to Order
                </Button>

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900 flex items-center gap-2">
                            <Palette className="h-6 w-6 text-purple-500" />
                            Artwork Review
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Order: {reviewData.order_title}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsShareDialogOpen(true)}
                            className="gap-1.5"
                            data-testid="share-with-designer-btn"
                        >
                            <Share2 className="h-4 w-4" />
                            Share for Review
                        </Button>
                        <Badge className={`${headerStatusLabel.color} gap-1.5 px-3 py-1.5 text-sm`} data-testid="artwork-review-status">
                            {headerStatusLabel.label}
                        </Badge>
                    </div>
                </div>

                {/* Rejection Banner */}
                {hasRejected && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="artwork-rejected-banner">
                        <div className="flex items-start gap-3">
                            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-red-900">
                                    {rejectedFiles.length === 1 
                                        ? '1 artwork file needs revision' 
                                        : `${rejectedFiles.length} artwork files need revision`}
                                </h3>
                                <p className="text-sm text-red-600 mt-1">
                                    Please upload corrected versions for the rejected files below. Approved files do not need to be re-uploaded.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Awaiting Approval Banner (for corrected files from repro)
                    — only shown while there's still at least one corrected
                    file pending the customer's decision. When all that's
                    left is files awaiting repro work, we surface the
                    "sent to repro" banner below instead. */}
                {canReview && !allRemainingAwaitRepro && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6" data-testid="artwork-attention-banner">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-medium text-purple-900">{t('artwork_review.corrected_ready', 'Corrected Artwork Ready for Your Approval')}</h3>
                                <p className="text-sm text-purple-600 mt-1">
                                    We've updated your artwork. Please review and approve or request further changes.
                                </p>
                                
                                {/* Repro partner message */}
                                {reviewData.partner_comment && (
                                    <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg" data-testid="repro-partner-message">
                                        <p className="text-xs font-semibold text-purple-800 mb-1.5">{t('artwork_review.message_label', 'Message about your artwork:')}</p>
                                        <p className="text-sm text-slate-700 whitespace-pre-line">{reviewData.partner_comment}</p>
                                    </div>
                                )}
                                
                                <div className="flex gap-2 mt-3">
                                    <Button
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => setIsApproveDialogOpen(true)}
                                        disabled={awaitingReproFiles.length > 0}
                                        title={awaitingReproFiles.length > 0
                                            ? 'Awaiting corrected versions from repro before the job can be approved'
                                            : undefined}
                                        data-testid="approve-corrected-btn"
                                    >
                                        <CheckCircle2 className="h-4 w-4 mr-2" /> Approve All
                                    </Button>
                                </div>
                                <p className="text-xs text-purple-500 mt-2">
                                    Need changes? Use the per-file <strong>Request Changes</strong> button below — you can
                                    approve some files and ask for revisions on others.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* "Sent to repro" banner — shown whenever the customer
                    has at least one replacement file waiting for repro. */}
                {awaitingReproFiles.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="awaiting-repro-banner">
                        <div className="flex items-start gap-3">
                            <Upload className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-medium text-amber-900">
                                    {awaitingReproFiles.length === 1
                                        ? '1 replacement file sent to repro'
                                        : `${awaitingReproFiles.length} replacement files sent to repro`}
                                </h3>
                                <p className="text-sm text-amber-700 mt-1">
                                    Your replacement artwork has been sent to repro for review/correction.
                                    We'll notify you by email as soon as the corrected version is ready
                                    for your approval.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Customer-uploaded replacement files — pending repro work. */}
                {awaitingReproFiles.length > 0 && (
                    <Card className="mb-6 border-amber-200 bg-amber-50/30" data-testid="awaiting-repro-section">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Upload className="h-5 w-5 text-amber-600" />
                                Your replacement files — awaiting repro
                            </CardTitle>
                            <CardDescription>
                                You uploaded new artwork. The repro partner has been notified and
                                will deliver a corrected version for your approval shortly.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {awaitingReproFiles.map((file) => (
                                <div
                                    key={file.id}
                                    className="p-3 rounded-lg border border-amber-200 bg-white flex items-start justify-between gap-3"
                                    data-testid={`awaiting-repro-file-${file.id}`}
                                >
                                    <div className="flex items-start gap-3 min-w-0">
                                        <FileIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate flex items-center gap-2">
                                                <span className="truncate">{file.filename}</span>
                                                <Badge className="bg-amber-500 text-white border-0 text-[10px]">
                                                    Awaiting repro
                                                </Badge>
                                            </p>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {formatFileSize(file.size)} · Uploaded by you
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadCorrected(file.id, file.filename)}
                                        data-testid={`awaiting-repro-download-${file.id}`}
                                        title="Download"
                                    >
                                        <Download className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {/* Corrected Artwork (primary focus) — each file has its
                    own Approve / Request Changes / Upload My Own actions. */}
                {reviewableCorrectedFiles.length > 0 && (
                    <Card className="mb-6" data-testid="corrected-files-section">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <FileImage className="h-5 w-5 text-green-500" />
                                Corrected Artwork
                            </CardTitle>
                            <CardDescription>
                                Review each file individually. Once every file is approved the job is
                                automatically released to the factory.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {reviewableCorrectedFiles
                                .filter((f) => !f.superseded)
                                .map((file) => {
                                    const reviewStatus = file.review_status || 'pending';
                                    const isApproved = reviewStatus === 'approved';
                                    const isRejected = reviewStatus === 'rejected';
                                    const isCustomerReplacement = file.source === 'customer_replacement';
                                    const acting = actingFileId === file.id;
                                    const supersededOriginal = correctedFiles.find(
                                        (f) => f.id === file.replaces_file_id,
                                    );
                                    return (
                                        <div
                                            key={file.id}
                                            className={`p-4 rounded-lg border ${
                                                isApproved
                                                    ? 'border-emerald-200 bg-emerald-50/40'
                                                    : isRejected
                                                    ? 'border-red-200 bg-red-50/40'
                                                    : 'border-slate-200 bg-white'
                                            }`}
                                            data-testid={`corrected-file-${file.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3 min-w-0">
                                                    <FileIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium truncate flex items-center gap-2">
                                                            <span className="truncate">{file.filename}</span>
                                                            {file.version > 1 && (
                                                                <span className="bg-indigo-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0">
                                                                    v{file.version}
                                                                </span>
                                                            )}
                                                            {isCustomerReplacement && (
                                                                <span
                                                                    className="bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                                                                    title="Replacement uploaded by customer"
                                                                >
                                                                    YOURS
                                                                </span>
                                                            )}
                                                            {isApproved && (
                                                                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                                                                    Approved
                                                                </Badge>
                                                            )}
                                                            {isRejected && (
                                                                <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                                                                    Changes requested
                                                                </Badge>
                                                            )}
                                                        </p>
                                                        <p className="text-xs text-slate-400 mt-0.5">
                                                            {formatFileSize(file.size)}
                                                            {supersededOriginal && (
                                                                <span className="ml-2 text-indigo-500">
                                                                    Replaces {supersededOriginal.filename}
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    {(file.filename || '').toLowerCase().endsWith('.pdf') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handlePreviewCorrected(file.id)}
                                                            data-testid={`preview-corrected-${file.id}`}
                                                            title="Preview PDF"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownloadCorrected(file.id, file.filename)}
                                                        data-testid={`download-corrected-${file.id}`}
                                                        title="Download"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>

                                            {file.comment && (
                                                <div
                                                    className="mt-3 p-2.5 bg-purple-50 border border-purple-100 rounded text-sm text-slate-700 whitespace-pre-line"
                                                    data-testid={`corrected-file-comment-${file.id}`}
                                                >
                                                    <span className="text-xs font-semibold text-purple-700 block mb-1">
                                                        Note from repro:
                                                    </span>
                                                    {file.comment}
                                                </div>
                                            )}

                                            {isRejected && file.review_comment && (
                                                <div
                                                    className="mt-3 p-2.5 bg-red-100/60 border border-red-200 rounded text-sm text-slate-700 whitespace-pre-line"
                                                    data-testid={`corrected-file-rejection-${file.id}`}
                                                >
                                                    <span className="text-xs font-semibold text-red-700 block mb-1">
                                                        Your feedback to repro:
                                                    </span>
                                                    {file.review_comment}
                                                    <p className="text-[11px] text-red-500 mt-1.5">
                                                        Awaiting a new corrected version from repro partner.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Action row — hidden when job is no longer awaiting
                                                approval OR file is already actioned. */}
                                            {canReview && !isApproved && !isRejected && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    <Button
                                                        size="sm"
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        disabled={acting}
                                                        onClick={() => handleApproveCorrectedFile(file.id)}
                                                        data-testid={`approve-corrected-file-${file.id}`}
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="border-red-200 text-red-700 hover:bg-red-50"
                                                        disabled={acting}
                                                        onClick={() => {
                                                            setPerFileRejectFor(file);
                                                            setPerFileComment('');
                                                        }}
                                                        data-testid={`reject-corrected-file-${file.id}`}
                                                    >
                                                        <XCircle className="h-4 w-4 mr-1.5" />
                                                        Request Changes
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={acting}
                                                        onClick={() => customerReplaceInputRefs.current[file.id]?.click()}
                                                        data-testid={`customer-replace-corrected-file-${file.id}`}
                                                    >
                                                        <Upload className="h-4 w-4 mr-1.5" />
                                                        Upload new
                                                    </Button>
                                                    <input
                                                        type="file"
                                                        ref={(el) => (customerReplaceInputRefs.current[file.id] = el)}
                                                        onChange={(e) => handleCustomerReplaceCorrected(file.id, e)}
                                                        className="hidden"
                                                        accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.psd,.ai,.eps"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                            {/* Show superseded versions collapsed for context */}
                            {correctedFiles.filter((f) => f.superseded).length > 0 && (
                                <details className="text-xs text-slate-400 pt-2">
                                    <summary className="cursor-pointer hover:text-slate-600">
                                        Earlier versions ({correctedFiles.filter((f) => f.superseded).length})
                                    </summary>
                                    <ul className="mt-2 space-y-1 pl-3 border-l border-slate-200">
                                        {correctedFiles
                                            .filter((f) => f.superseded)
                                            .map((f) => (
                                                <li key={f.id} className="flex items-center gap-2">
                                                    <FileIcon className="h-3 w-3" />
                                                    <span>
                                                        {f.filename} <span className="text-slate-300">— superseded</span>
                                                    </span>
                                                </li>
                                            ))}
                                    </ul>
                                </details>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Original Files (secondary / reference) */}
                <Card className="mb-6 opacity-80" data-testid="artwork-files-section">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base text-slate-500">
                            <FileImage className="h-4 w-4 text-slate-400" />
                            Original Files
                        </CardTitle>
                        <CardDescription>
                            Your originally submitted artwork
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {artworkFiles.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">{t('artwork_review.no_files_yet', 'No artwork files uploaded yet')}</p>
                        ) : (
                            artworkFiles.map((file) => {
                                const status = file.review_status || 'pending';
                                const config = FILE_STATUS_CONFIG[status] || FILE_STATUS_CONFIG.pending;
                                const StatusIcon = config.icon;
                                const isRejected = status === 'rejected';
                                
                                return (
                                    <div 
                                        key={file.id} 
                                        className={`p-4 rounded-lg border ${config.border}`}
                                        data-testid={`customer-file-${file.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded ${status === 'approved' ? 'bg-emerald-100' : status === 'rejected' ? 'bg-red-100' : 'bg-slate-100'}`}>
                                                    <FileIcon className={`h-5 w-5 ${status === 'approved' ? 'text-emerald-600' : status === 'rejected' ? 'text-red-600' : 'text-slate-400'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-slate-900">{file.filename}</p>
                                                    <p className="text-xs text-slate-400">
                                                        {formatFileSize(file.size)}
                                                        {file.version > 1 && <span className="ml-1 text-indigo-500 font-medium">(v{file.version})</span>}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {(file.filename || '').toLowerCase().endsWith('.pdf') && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={async () => {
                                                                try {
                                                                    const token = localStorage.getItem('token');
                                                                    const r = await fetch(`${API_BASE}/api/files/${file.id}/preview-url`, {
                                                                        headers: { 'Authorization': `Bearer ${token}` }
                                                                    });
                                                                    if (!r.ok) throw new Error('Preview failed');
                                                                    const d = await r.json();
                                                                    if (d.preview_url) window.open(d.preview_url, '_blank', 'noopener');
                                                                } catch { toast.error(t('artwork_review.preview_failed', 'Failed to open preview')); }
                                                            }}
                                                            title="Preview PDF"
                                                            data-testid={`customer-file-preview-${file.id}`}
                                                            className="h-8 w-8 p-0"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={async () => {
                                                                try {
                                                                    const token = localStorage.getItem('token');
                                                                    const r = await fetch(`${API_BASE}/api/files/${file.id}/download`, {
                                                                        headers: { 'Authorization': `Bearer ${token}` }
                                                                    });
                                                                    const ct = r.headers.get('content-type') || '';
                                                                    if (ct.includes('application/json')) {
                                                                        const d = await r.json();
                                                                        if (d.download_url) window.open(d.download_url, '_blank');
                                                                    } else if (r.ok) {
                                                                        const blob = await r.blob();
                                                                        const url = window.URL.createObjectURL(blob);
                                                                        const link = document.createElement('a');
                                                                        link.href = url;
                                                                        link.setAttribute('download', file.filename || 'artwork');
                                                                        document.body.appendChild(link);
                                                                        link.click();
                                                                        link.remove();
                                                                        window.URL.revokeObjectURL(url);
                                                                    }
                                                                } catch { toast.error(t('artwork_review.download_failed', 'Failed to download file')); }
                                                            }}
                                                            title="Download"
                                                            data-testid={`customer-file-download-${file.id}`}
                                                            className="h-8 w-8 p-0"
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                {status !== 'pending' && (
                                                    <Badge className={`${config.color} gap-1`} data-testid={`customer-file-status-${file.id}`}>
                                                        <StatusIcon className="h-3 w-3" />
                                                        {config.label}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Rejection feedback */}
                                        {isRejected && file.rejection_comment && (
                                            <div className="mt-3 p-3 bg-red-100 rounded-lg" data-testid={`rejection-comment-${file.id}`}>
                                                <p className="text-xs font-semibold text-red-800 mb-1">{t('artwork_review.feedback_label', 'Feedback:')}</p>
                                                <p className="text-sm text-red-700">{file.rejection_comment}</p>
                                            </div>
                                        )}
                                        
                                        {/* Upload replacement CTA for rejected files */}
                                        {isRejected && (
                                            <div className="mt-3 pt-3 border-t border-red-200">
                                                <input
                                                    type="file"
                                                    ref={(el) => { fileInputRefs.current[file.id] = el; }}
                                                    multiple
                                                    accept="image/*,.pdf,.ai,.eps,.indd,.psd,.tif,.tiff"
                                                    onChange={(e) => handleReplaceFile(file.id, e)}
                                                    className="hidden"
                                                />
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                                                    onClick={() => fileInputRefs.current[file.id]?.click()}
                                                    disabled={uploadingFileId === file.id}
                                                    data-testid={`replace-file-${file.id}`}
                                                >
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    {uploadingFileId === file.id 
                                                        ? 'Uploading...' 
                                                        : `Upload replacement for ${file.filename}`}
                                                </Button>
                                            </div>
                                        )}
                                        
                                        {/* Version history for this file */}
                                        {(() => {
                                            const prevVersions = getVersionHistory(file.id);
                                            if (prevVersions.length === 0) return null;
                                            return (
                                                <details className="mt-3 pt-3 border-t border-slate-200">
                                                    <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 select-none">
                                                        Show previous version{prevVersions.length > 1 ? 's' : ''} ({prevVersions.length})
                                                    </summary>
                                                    <div className="mt-2 space-y-2">
                                                        {prevVersions.map((pv) => (
                                                            <div key={pv.id} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100 opacity-70">
                                                                <div className="flex items-center gap-2">
                                                                    <FileIcon className="h-4 w-4 text-slate-300" />
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">{pv.filename} <span className="text-slate-400">(v{pv.version || 1})</span></p>
                                                                        {pv.rejection_comment && (
                                                                            <p className="text-[10px] text-red-400 mt-0.5">Rejected: {pv.rejection_comment}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Badge className="bg-slate-100 text-slate-400 text-[10px]">
                                                                    {pv.review_status === 'rejected' ? 'Rejected' : 'Superseded'}
                                                                </Badge>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </details>
                                            );
                                        })()}
                                    </div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>

                {/* Approve Dialog */}
                <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
                    <DialogContent data-testid="approve-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('artwork_review.approve_dialog_title', 'Approve Corrected Artwork')}</DialogTitle>
                            <DialogDescription>
                                Confirm the corrected artwork is satisfactory and ready for production.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Label>Comments (optional)</Label>
                            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Any notes for the production team..." rows={3} className="mt-2" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsApproveDialogOpen(false)}>Cancel</Button>
                            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={isSubmitting} data-testid="confirm-approve-btn">
                                {isSubmitting ? 'Approving...' : 'Approve Artwork'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Request Changes Dialog (legacy global flow — still used
                    by the original artwork rejection path). */}
                <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
                    <DialogContent data-testid="reject-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('artwork_review.request_changes', 'Request Changes')}</DialogTitle>
                            <DialogDescription>
                                Describe what needs to be changed in the corrected artwork.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Label>Feedback *</Label>
                            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Describe the changes needed..." rows={4} className="mt-2" data-testid="reject-feedback-input" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>Cancel</Button>
                            <Button variant="destructive" onClick={handleRequestChanges} disabled={isSubmitting || !comment.trim()} data-testid="confirm-reject-btn">
                                {isSubmitting ? 'Submitting...' : 'Submit Change Request'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Per-file Request Changes Dialog (for a specific corrected
                    artwork file). Submits feedback scoped to that file only. */}
                <Dialog
                    open={!!perFileRejectFor}
                    onOpenChange={(open) => {
                        if (!open) {
                            setPerFileRejectFor(null);
                            setPerFileComment('');
                        }
                    }}
                >
                    <DialogContent data-testid="reject-corrected-file-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('artwork_review.request_changes', 'Request Changes')}</DialogTitle>
                            <DialogDescription>
                                {perFileRejectFor ? (
                                    <>
                                        Feedback for <strong className="text-slate-900">{perFileRejectFor.filename}</strong>.
                                        The repro partner will be notified and can upload a new corrected
                                        version for this file.
                                    </>
                                ) : (
                                    'Describe what needs to be changed.'
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-2">
                            <Label>Feedback *</Label>
                            <Textarea
                                value={perFileComment}
                                onChange={(e) => setPerFileComment(e.target.value)}
                                placeholder="Describe what needs to be fixed in this file..."
                                rows={4}
                                className="mt-2"
                                data-testid="reject-corrected-file-feedback"
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPerFileRejectFor(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleRejectCorrectedFileSubmit}
                                disabled={isSubmitting || !perFileComment.trim()}
                                data-testid="confirm-reject-corrected-file-btn"
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Designer comments — Phase 2 of external-designer collaboration.
                    External designers post feedback via a share link; the comments
                    are stored on the artwork job and surfaced here so the
                    customer and admin see the conversation alongside the repro
                    feedback they already have. */}
                {Array.isArray(reviewData.designer_comments) && reviewData.designer_comments.length > 0 && (
                    <Card className="mt-6" data-testid="designer-comments-card">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <MessageSquare className="h-5 w-5 text-purple-500" />
                                Designer comments
                                <span className="text-xs font-normal text-slate-400">
                                    ({reviewData.designer_comments.length})
                                </span>
                            </CardTitle>
                            <CardDescription>
                                Feedback left by your external designer via the share link.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {reviewData.designer_comments.map((c) => (
                                <div
                                    key={c.id}
                                    className="border border-slate-200 rounded-lg p-3 bg-slate-50/40"
                                    data-testid={`designer-comment-${c.id}`}
                                >
                                    <div className="flex items-baseline justify-between gap-2 mb-1">
                                        <span className="text-sm font-semibold text-slate-800">
                                            {c.author_name}
                                        </span>
                                        <span className="text-[11px] text-slate-400">
                                            {new Date(c.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {c.author_email && (
                                        <p className="text-[11px] text-slate-400 mb-2">{c.author_email}</p>
                                    )}
                                    <p className="text-sm text-slate-700 whitespace-pre-line">
                                        {c.message}
                                    </p>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                )}

                <ShareArtworkDialog
                    orderId={orderId}
                    open={isShareDialogOpen}
                    onOpenChange={setIsShareDialogOpen}
                />
            </main>
        </div>
    );
}
